import { FSM, MiddleState } from 'afsm';
import EventEmitter from 'eventemitter3';
import {
  ChannelEvent,
  SendSignalToLog,
  SignalCommand,
  SignalConnectionState,
  SignalEvent,
  SignalEventMap,
  SignalEventsForLogIgnored
} from './constant';
import RtcError from 'trtc-js-sdk-core/src/common/rtc-error';
import ErrorCode from 'trtc-js-sdk-core/src/common/error-code';
import { NAME } from 'trtc-js-sdk-core/src/common/constants';
import { getStringByteLength, isUndefined, performanceNow, promiseAny } from 'trtc-js-sdk-core/src/utils/utils';
import { generateErrorMessage } from 'trtc-js-sdk-core/src/common/error-generator';
import { ErrorName } from 'trtc-js-sdk-core/src/common/error-info';
import loggerManager from 'trtc-js-sdk-core/src/utils/log/logger-manager';
import promiseRetry from 'trtc-js-sdk-core/src/utils/retry';
import { IRoom, Logger, SendWaitForResponseParams, SendWaitForResponseWithRetryParams, setNetworkTimeOffset, SignalResponse } from 'trtc-js-sdk-core';
import { Room } from '../room';
import { addPromiseRetry } from 'trtc-js-sdk-core/src/utils/decorators/promise-retry';
import { setIsNeedToSchedule } from 'trtc-js-sdk-core/src/utils/schedule';
import kvStatManager, { KV_REPORT_KEY_NETWORK, KV_REPORT_KEY_WHIP } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';


interface SignalChannelOptions {
  sdkAppId: number,
  userId: string,
  userSig: string,
  url: string,
  backupUrl: string,
  room: Room,
  signalDomainWhenUnifiedProxy?: string | null;
  preconnect?: boolean;
  whipRebuildParams?: {
    roomId: string;
    useStringRoomId: boolean;
    relayInnerIp: string;
    relayOuterIp: string;
    relayPort: number;
  };
}

interface PreconnectConfig {
  sdkAppId: number;
  userId: string;
  userSig: string;
  linkedTime?: number;
}

// 记录开启了 keepAlive 的SignalChannel 实例
const keepAliveSignalChannelSet: Set<SignalChannel> = new Set();

// 获取空闲可用的 SignalChannel 实例，用于同 userId，使用 trtc 多实例场景进房时，复用 SignalChannel
export function getActiveKeepAliveSignalChannel(userId: string) {
  const keepAliveSignalChannel = [...keepAliveSignalChannelSet.values()].find(signalChannel => signalChannel.room.userId === userId && !signalChannel.room.isJoined);
  if (keepAliveSignalChannel) {
    return keepAliveSignalChannel;
  }
  return null;
}

const BACKEND_URL_PARAMS = ['autoTest', 'relayInnerIp', 'relayOuterIp', 'mcd', 'newRelay', 'clientIp'];

let wsSeq = 0;
/**
 *
 *
 * @class SignalChannel
 */
export default class SignalChannel extends EventEmitter {
  room: Room;
  sdkAppId: number;
  userId: string;
  userSig: string;
  url: string;
  backupUrl: string;
  destroyed = false;
  private _socketInUse: WebSocket | null;
  private _socket: WebSocket | null;
  private _backupSocket: WebSocket | null;
  private _signalInfo: SignalInfo = {
    tinyId: undefined,
    clientIp: '',
    signalIp: '',
    relayIp: '',
    relayInnerIp: '',
    relayPort: 0,
    endReportExtend: undefined,
    bakRelayIps: [],
    reportToken: undefined
  };
  private _currentState = SignalConnectionState.DISCONNECTED;
  private _isReconnecting = false;
  private _seq = 0;
  private _log: Logger;

  private _lastMessageTime = -1;
  protected _connectStartTime = -1;
  private _stopConnectRetry?: () => void;
  private _isFirstConnect = true;

  bytesSent = 0;
  bytesReceived = 0;
  keepAlive = false;
  signalDomainWhenUnifiedProxy?: string | null;
  stopKeepAliveTimeout: number;
  stopPreconnectTimeout: number;
  private _whipRebuildParams?: SignalChannelOptions['whipRebuildParams'];

  rtt = 0; // 先用获取用户列表的信令往返耗时代替 rtt
  hasPreconnect = false; // 是否有已预连接、且还未发进房信令的 ws
  private _preconnectConfig?: PreconnectConfig; // preconnect 配置
  get race() {
    return this.room.enableSPC && !this.room.proxy_ws;
  }
  get urlParam() {
    let result = `?sdkAppId=${encodeURIComponent(this.sdkAppId)}&userId=${encodeURIComponent(this.userId)}&userSig=${encodeURIComponent(this.userSig)}&keepAlive=${encodeURIComponent(Number(this.keepAlive))}`;
    // 设置统一代理时，在 url 参数中填入信令调度返回的主域名，后台根据该参数选择接入的信令服务器。
    if (this.signalDomainWhenUnifiedProxy) {
      result += `&signalDomain=${encodeURIComponent(this.signalDomainWhenUnifiedProxy)}`;
    }
    if (this.hasPreconnect) {
      result += '&prelink=1';
    }
    const urlSearchParams = new URLSearchParams(location.search);
    BACKEND_URL_PARAMS.forEach(key => {
      const value = urlSearchParams.get(`trtc_${key}`);
      if (value) result += `&${key}=${encodeURIComponent(value)}`;
    });
    if (this._whipRebuildParams) {
      result += `&rc=1&relayInnerIp=${encodeURIComponent(this._whipRebuildParams.relayInnerIp)}&relayOuterIp=${encodeURIComponent(this._whipRebuildParams.relayOuterIp)}&relayPort=${encodeURIComponent(this._whipRebuildParams.relayPort)}&roomId=${encodeURIComponent(this._whipRebuildParams.roomId)}&useStringRoomId=${encodeURIComponent(this._whipRebuildParams.useStringRoomId)}`;
    }
    if (this.race) {
      return `${result}&race=1`;
    }
    return result;
  }

  get _urlWithParam() {
    return `${this.url}${this.race ? '/v2/ws' : ''}${this.urlParam}`;
  }

  get _backupUrlWithParam() {
    return `${this.backupUrl}${this.race ? '/v2/ws' : ''}${this.urlParam}`;
  }

  get isConnected() {
    return this._currentState === SignalConnectionState.CONNECTED;
  }
  get isConnecting() {
    return this._currentState === SignalConnectionState.CONNECTING;
  }

  constructor(options: SignalChannelOptions) {
    super();
    this.room = options.room;
    this.sdkAppId = options.sdkAppId;
    this.userId = options.userId;
    this.userSig = options.userSig;
    this.signalDomainWhenUnifiedProxy = options.signalDomainWhenUnifiedProxy;
    this.hasPreconnect = options.preconnect || false;
    this._whipRebuildParams = options.whipRebuildParams;
    let keepAliveCount = this.room.scheduleResult?.config?.keepAliveClient || 0;
    // @ts-ignore 隐藏参数，用于自动化测试。
    if (this.room.joinParams?.keepAlive && !keepAliveCount) keepAliveCount = 1;
    // 只在单 PC 开启 keepAlive
    if (keepAliveCount - keepAliveSignalChannelSet.size > 0 && this.room.enableSPC) {
      this.keepAlive = true;
      keepAliveSignalChannelSet.add(this);
    }
    this.url = options.url;
    this.backupUrl = options.backupUrl;

    this._seq = 0;
    this._log = loggerManager.createLogger({
      parent: this.room.getLogger(),
      id: `ws${++wsSeq}`,
      userId: this.userId,
      sdkAppId: this.sdkAppId
    });

    this.onmessage = this.onmessage.bind(this);
    this.onerror = this.onerror.bind(this);
    this.onclose = this.onclose.bind(this);
  }

  /**
   * 判断 websocket 是否 online
   * 1. websocket 处于连接状态
   * 2. 最近一条收到的信令不超过 12s
   *
   * 取 12s 的原因：
   *
   * case 信息：https://tapd.woa.com/TerWebG/prong/stories/view/1020396022880216403
   *
   * 处理这个 case 经历了 3步：
   *
   * 1. 原来是判断所有 pc 重连超过6次，则认为 relay 可能宕机了，实际上测试发现断网也可能触发这个条件。
   * 2. 优化判断条件，改为 所有 pc 重连 6次，且 Websocket 处于连接状态。实际测试发现，拔网线后，可能出现所有 pc 重连 6次了，但是 Websocket 还没断的情况。
   * 3. 优化判断条件，改为 所有 pc 重连 6次，且信令处于 online 状态。
   * 因此此处增加了 isOnline 的判断，这个 isOnline 只在判断 relay 可能宕机的时候用到。
   * Websocket 断连可能会耗时 15 ~ 30s，后台定时 2s 会推送信令下来，这里取了个折中值。
   */
  get isOnline() {
    return this._currentState === SignalConnectionState.CONNECTED &&
      Date.now() - this._lastMessageTime < 12 * 1000;
  }

  /**
   * Connect to Signal Server via WebSocket
   *
   * @param {number} [timeout] 超时时间，默认没有超时时间，连不上则无限重连。
   * @memberof SignalChannel
   */
  @addPromiseRetry({
    settings: { retries: Infinity, timeout: 2000 },
    onError(this: SignalChannel, error, retry) {
      if (!this.room.isDestroyed && !this.destroyed) {
        if (this._isFirstConnect) {
          kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.WS_CONNECT, error });
          this._isFirstConnect = false;
        }
        // 每次连接失败时增加超时计数
        this.room.firewallDetector.increaseTimeoutCount();
        retry();
      }
    },
    onRetrying(this: SignalChannel, retryCount, stopRetry) {
      this._log.warn(`retrying to connect ${retryCount}`);
      if (retryCount >= 3 && retryCount % 3 === 0) {
        this.reGetSignalChannelUrl();
      }
      if (stopRetry) {
        this._stopConnectRetry = stopRetry;
        if (this.room.isDestroyed || this.destroyed) {
          stopRetry();
        }
      }
    }
  })
  async connect(timeout = 10 * 1000): Promise<void> {
    if (this.isConnected) return Promise.resolve();

    this._log.info(`connect to [${this.url}, ${this.backupUrl}] ${this.race ? 'race' : ''}${timeout ? ` timeout: ${timeout}` : ''} keepAlive: ${Number(this.keepAlive)}`);
    this.emitConnectionStateChanged(SignalConnectionState.CONNECTING);


    this._connectStartTime = performanceNow();
    const promiseList = [this.connectWS({ url: this._urlWithParam, isMain: true, timeout })];
    if (this.race && this._backupUrlWithParam !== this._urlWithParam) {
      promiseList.push(this.connectWS({ url: this._backupUrlWithParam, isMain: false, timeout }));
    }
    // 主备同时连接，竞速。
    this._socketInUse = await promiseAny(promiseList) as WebSocket;
    // 竞速连接成功后，将另外一个 ws 断开。
    this.unbindAndCloseSocket(this._socketInUse === this._socket ? NAME.BACKUP : NAME.MAIN);

    if (this._isFirstConnect) {
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.WS_CONNECT });
      this._isFirstConnect = false;
    }
    this.emitConnectionStateChanged(SignalConnectionState.CONNECTED);
  }

  connectWS({ url, timeout, isMain }: { url: string, timeout: number; isMain: boolean }) {
    const socket = new WebSocket(url);
    this.bindSocket(socket);
    if (isMain) {
      this._socket = socket;
    } else {
      this._backupSocket = socket;
    }
    let timeoutId = -1;
    return new Promise((resolve, reject) => {
      socket.onclose = reject;
      socket.onerror = reject;
      socket.onopen = () => resolve(socket);

      if (timeout) {
        timeoutId = setTimeout(() => {
          this.unbindAndCloseSocket(isMain ? NAME.MAIN : NAME.BACKUP);
          reject(new RtcError({
            code: ErrorCode.SIGNAL_CHANNEL_SETUP_FAILED,
            message: 'ws connect timeout'
          }));
        }, timeout);
      }
    }).finally(() => {
      socket.onclose = null;
      socket.onerror = null;
      socket.onopen = null;
      clearTimeout(timeoutId);
    });
  }

  /**
   * bind WebSocket callbacks
   *
   * @private
   * @param {WebSocket} socket
   * @memberof SignalChannel
   */
  bindSocket(socket: WebSocket) {
    socket.addEventListener('close', this.onclose);
    socket.addEventListener('error', this.onerror);
    socket.addEventListener('message', this.onmessage);
  }

  unbindSocket(socket: WebSocket) {
    socket.removeEventListener('close', this.onclose);
    socket.removeEventListener('error', this.onerror);
    socket.removeEventListener('message', this.onmessage);
  }

  unbindAndCloseSocket(type: string) {
    if (type === NAME.MAIN) {
      if (this._socket) {
        this.unbindSocket(this._socket);
        try {
          this._socket.close(1000);
        } catch (error) {
          // ignore
        }
        this._socket = null;
      }
    } else {
      if (this._backupSocket) {
        this.unbindSocket(this._backupSocket);
        try {
          this._backupSocket.close(1000);
        } catch (error) {
          // ignore
        }
        this._backupSocket = null;
      }
    }
  }

  /**
   * WebSocket.onclose callback
   *
   * @private
   * @param {CloseEvent} event
   * @memberof SignalChannel
   */
  onclose(event: any) {
    if (event.target !== this._socketInUse) return;
    this._log.warn(`${event.target === this._socket ? 'main' : 'backup'} is closed code:${event.code} ${event.reason}`);
    this.emitConnectionStateChanged(SignalConnectionState.DISCONNECTED);
    // 1000 is considered as normal close
    // 4013 is considered as closed by server due to timeout
    if (!event.wasClean || (event.code !== 1000 && event.code !== 4013)) {
      this.startReconnection();
    }
    if (this.hasPreconnect && event.code === 4013) {
      this.room.clearNetworkQuality();
    }
    // 进房过程中可能出现 ws 连上过几毫秒又断开的情况。
    if (this.room.isJoining) {
      this.emit(ChannelEvent.SETUP_FAILED, new RtcError({
        code: ErrorCode.SIGNAL_CHANNEL_SETUP_FAILED,
        message: 'websocket onclose'
      }));
    }
  }

  /**
   * WebSocket.onerror callback
   *
   * @private
   * @param {Event} event
   * @memberof SignalChannel
   */
  onerror(event: any) {
    this._log.error(`${event.target === this._socket ? 'main' : 'backup'} error observed`);
    this.emitConnectionStateChanged(SignalConnectionState.DISCONNECTED);
    if (event.target === this._socketInUse) {
      this.unbindAndCloseSocket(NAME.MAIN);
      this.unbindAndCloseSocket(NAME.BACKUP);
      this._socketInUse = null;
      this.reconnect();
    }
    if (this.room.isJoining) {
      this.emit(ChannelEvent.SETUP_FAILED, new RtcError({
        code: ErrorCode.SIGNAL_CHANNEL_SETUP_FAILED,
        message: 'websocket onerror'
      }));
    }
  }

  /**
   * WebSocket.onmessage callback
   *
   * @private
   * @param {MessageEvent} event the MessageEvent received from WebSocket
   * @memberof SignalChannel
   */
  onmessage(event: any) {
    if (!this.isConnected) return; // close was requested.
    const { isOnline } = this;
    this._lastMessageTime = Date.now();
    if (!isOnline) this.emit(ChannelEvent.ONLINE);
    this.bytesReceived += getStringByteLength(event.data);
    const data = JSON.parse(event.data);
    const { cmd, data: content } = data;
    const signalEventValues = Object.values(SignalEventMap);
    const signalEventKeys = Object.keys(SignalEventMap);
    const index = signalEventKeys[signalEventValues.indexOf(cmd)];
    // @ts-ignore
    const eventName = SignalEvent[index] || cmd;
    // filter out the annoying message
    if (!SignalEventsForLogIgnored.includes(cmd)) {
      this._log.debug(`received ${cmd} msg: ${event.data}`);
      if (eventName) {
        this._log.info(`Received event: [ ${eventName} ]`);
      }
    }

    switch (cmd) {
      case SignalEventMap.CHANNEL_SETUP_RESULT: {
        // WS 建立成功
        if (data.code === 0) {
          this._signalInfo.clientIp = content.clientIp;
          this._signalInfo.signalIp = content.signalInnerIp;

          if (content.svrTime) {
            setNetworkTimeOffset(content.svrTime - new Date().getTime());
          }

          this._log.info(`ChannelSetup Success ${performanceNow() - this._connectStartTime}`);
          kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.SIGNAL_CHANNEL_SETTUP, cost: performanceNow() - this._connectStartTime });
          this._connectStartTime = -1;
          // WebSocket 连接成功，重置防火墙超时计数
          this.room.firewallDetector.resetTimeoutCount();
          this.emit(ChannelEvent.SETUP_SUCCESS, { signalInfo: this._signalInfo });
        } else {
          const error = new RtcError({
            code: ErrorCode.SIGNAL_CHANNEL_SETUP_FAILED,
            extraCode: data.code,
            message: generateErrorMessage({
              key: ErrorName.SIGNAL_CHANNEL_SETUP_FAILED,
              data: { errorCode: data.code, errorMsg: data.message }
            })
          });
          this._log.error(`${data.code}, ${data.message}`);
          // 信令建连失败，关闭连接
          this.close();
          kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.SIGNAL_CHANNEL_SETTUP, error });
          this.emit(ChannelEvent.SETUP_FAILED, error);
        }
        break;
      }
      case SignalEventMap.CHANNEL_RECONNECT_RESULT: {
        // whip 拉流 rebuild ws 收不到 cmd 19, 使用 cmd 514 替代
        if (this._whipRebuildParams) {
          if (data.code === 0) {
            this._signalInfo.clientIp = content.clientIp;
            this._signalInfo.signalIp = content.signalInnerIp;

            if (content.svrTime) {
              setNetworkTimeOffset(content.svrTime - new Date().getTime());
            }

            this._log.info(`ChannelReconnect Success, clientIp: ${content.clientIp}, signalInnerIp: ${content.signalInnerIp}`);
            // WebSocket 连接成功，重置防火墙超时计数
            this.room.firewallDetector.resetTimeoutCount();
            kvStatManager.addEnum({ key: KV_REPORT_KEY_WHIP.WHIP_REBUILD_RESULT, value: 0 });
            this.emit(ChannelEvent.SETUP_SUCCESS, { signalInfo: this._signalInfo });
          } else {
            this._log.error(`whip rebuild failed: ${data.code}, ${data.message}`);
            kvStatManager.addEnum({ key: KV_REPORT_KEY_WHIP.WHIP_REBUILD_RESULT, value: 1 });
            this.startReconnection();
          }
          delete this._whipRebuildParams;
        }
        this.emit(eventName, { data });
        break;
      }
      case SignalEventMap.JOIN_ROOM_RESULT: {
        if (data.code === 0) {
          this._signalInfo.relayIp = content.relayOuterIp;
          this._signalInfo.relayInnerIp = content.relayInnerIp;
          this._signalInfo.bakRelayIps = content.bakRelayIps;
          this._signalInfo.relayPort = content.relayPort;
          this._signalInfo.tinyId = data.tinyId;
          this._signalInfo.endReportExtend = content.endReportExtend;
          this._signalInfo.reportToken = content.reportToken;
          this._log.info(`signalIp:${this._signalInfo.signalIp} clientIp:${this._signalInfo.clientIp} relayIp: ${this._signalInfo.relayIp}`);
        }
        this.emit(eventName, { data });
        break;
      }
      default:
        this.emit(String(eventName), {
          /** @type {MessageEvent.data} */ data
        });
        break;
    }
  }

  /**
   * 用于重连失败多次后，重新调度获取信令地址。
   */
  async reGetSignalChannelUrl() {
    try {
      if (!this.room.joinParams) return;
      setIsNeedToSchedule(true);
      await this.room.schedule(this.room.joinParams);
      const { mainUrl, backupUrl } = this.room.getSignalChannelUrl();
      this.url = mainUrl;
      this.backupUrl = backupUrl;
    } catch (error) {
      // ignore
    }
  }

  startReconnection() {
    if (!this._socketInUse) return;
    this._socketInUse.onclose = null;
    // 4011 indicates that we want reconnect with new WebSocket
    this._socketInUse.close(4011);

    const isMainSocketClosed = this._socketInUse === this._socket;

    this.unbindAndCloseSocket(isMainSocketClosed ? NAME.MAIN : NAME.BACKUP);
    this._socketInUse = null;
    this.emitConnectionStateChanged(SignalConnectionState.DISCONNECTED);

    this.reconnect();
  }

  /**
   * reconnect to signal server
   * @private
   * @memberof SignalChannel
   */
  async reconnect() {
    if (this._isReconnecting) return;
    // 退房后，keepAlive 的 ws 断连，此时不能重连，直接 close。下次进房重新建立 ws。
    if (!this.room.isJoined && this.keepAlive) {
      this.close();
      return;
    }
    this._isReconnecting = true;

    try {
      this._log.warn('reconnect');
      await this.connect();
      const { roomId, useStringRoomId } = this.room;
      const { relayIp: relayOuterIp, relayInnerIp, relayPort } = this._signalInfo;

      const { data } = await this.sendWaitForResponse({
        command: SignalCommand.RECONNECT_WS,
        data: { roomId, useStringRoomId, relayInnerIp, relayOuterIp, relayPort },
        responseCommand: SignalEvent.CHANNEL_RECONNECT_RESULT
      });
      if (data.code === 0) {
        this._log.warn('reconnect success');
        this.stopReconnection();
        kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.WS_RECONNECT, cost: performanceNow() - this._connectStartTime });
        this._connectStartTime = -1;
        // WS 重连成功后，需同步一次用户列表
        this.room.syncUserList();
        this.room.checkConnectionsToReconnect();
      } else {
        kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.WS_RECONNECT, error: data.code });
        // WS 重连失败，走重进房逻辑。
        this._log.warn(`reconnect failed, ${data.code} ${data.message}`);
        this.room.reJoin();
      }
    } catch (error) {
      this._log.error(error);
      this.room.reJoin();
    }
  }

  /**
   * Send command to signal server
   *
   * @param {string} command the signal command sent to signal server
   * @param {object} data the data of the command
   * @memberof SignalChannel
   */
  send(command: string, data = {}) {
    // 已退房的情况下，不发任何信令
    if (this.isConnected && !this.room.isLeft) {
      const sendMessage = {
        cmd: command,
        data,
        userId: this.userId,
        tinyId: this._signalInfo.tinyId,
        seq: ++this._seq
      };
      const stringifyData = JSON.stringify(sendMessage);
      this._socketInUse!.send(stringifyData);

      if (SendSignalToLog.has(command as any)) {
        this._log.info('send', command, data);
      }
      this.bytesSent += getStringByteLength(stringifyData);
      return sendMessage.seq;
    }
  }

  /**
   * 发送 command 直至收到对应的 response 才 resolve，否则超时 reject
   *
   * @param {String} command 待发送的信令名
   * @param {String} [commandDesc] 信令描述，用于填写超时的错误描述
   * @param {*} data 待发送的数据
   * @param {Number} [timeout=5000] 超时时间
   * @param {String} responseCommand 后台响应的信令名
   * @param {Boolean} [enableLog=true] 是否在信令超时时，输出 warn 日志
   * @return {Promise}
   * @memberof SignalChannel
   */

  sendWaitForResponse({
    command,
    data,
    timeout = 5000,
    responseCommand,
    commandDesc,
    enableLog = true,
    addReceiveTime = false
  }: SendWaitForResponseParams) : Promise<SignalResponse> {
    return new Promise((resolve, reject) => {
      const onClose = () => {
        clearTimeout(timeoutId);
        reject(new RtcError({
          code: ErrorCode.API_CALL_ABORTED,
          message: `${command} aborted due to connection closed`
        }));
      };
      this.once(ChannelEvent.CLOSE, onClose);
      const timeoutId = setTimeout(() => {
        this.off(responseCommand, handleResult);
        this.off(ChannelEvent.CLOSE, onClose);
        const error = new RtcError({
          code: ErrorCode.API_CALL_TIMEOUT,
          message: generateErrorMessage({
            key: ErrorName.API_CALL_TIMEOUT,
            data: { commandDesc, command }
          })
        });
        enableLog && this._log.warn(error);
        reject(error);
      }, timeout);
      const handleResult = (event: any) => {
        if (event.data.seq === seq) {
          clearTimeout(timeoutId);
          this.off(responseCommand, handleResult);
          this.off(ChannelEvent.CLOSE, onClose);
          if (addReceiveTime) {
            event.data.receiveTime = Date.now();
          }
          resolve(event);
        }
      };
      this.on(responseCommand, handleResult);
      const seq = this.send(command, data);
    });
  }

  sendWaitForResponseWithRetry(params: SendWaitForResponseWithRetryParams) {
    const { commandDesc, command, retries = 0, retryTimeout = 0 } = params;
    return promiseRetry({
      retryFunction: this.sendWaitForResponse,
      onError: ({ retry, reject, error }) => {
        if (!this.room.isJoined || this.destroyed || error.code === ErrorCode.API_CALL_ABORTED) {
          reject(error);
        } else if (this.isOnline) {
          retry();
        } else {
          this._log.warn(`retry ${command} when connected`);
          this.once(ChannelEvent.ONLINE, retry);
        }
      },
      onRetrying: retryCount => {
        this._log.warn(`${commandDesc || command} timeout observed, retrying [${retryCount}/${retries}]`);
      },
      settings: {
        retries,
        timeout: retryTimeout
      },
      context: this
    })(params);
  }

  getCurrentState() {
    return this._currentState;
  }

  getSignalInfo() {
    return this._signalInfo;
  }

  stopReconnection() {
    this._isReconnecting = false;
    if (this._stopConnectRetry) {
      this._stopConnectRetry();
    }
  }

  /**
* close the SignalChannel
*
* @memberof SignalChannel
*/
  close() {
    this._log.info('closed');
    clearTimeout(this.stopKeepAliveTimeout);
    clearTimeout(this.stopPreconnectTimeout);
    keepAliveSignalChannelSet.delete(this);
    this.stopReconnection();
    this._signalInfo = {
      tinyId: undefined,
      clientIp: '',
      signalIp: '',
      relayIp: '',
      relayInnerIp: '',
      relayPort: 0,
      bakRelayIps: [],
      endReportExtend: undefined,
      reportToken: undefined
    };
    this._socketInUse = null;

    this.bytesSent = 0;
    this.bytesReceived = 0;
    if (this._stopConnectRetry) {
      this._stopConnectRetry();
    }
    this.unbindAndCloseSocket(NAME.MAIN);
    this.unbindAndCloseSocket(NAME.BACKUP);
    this.emitConnectionStateChanged(SignalConnectionState.DISCONNECTED);
    this.emit(ChannelEvent.CLOSE);
  }

  destroy() {
    this.close();
    this.destroyed = true;
  }

  getBackupRelayIpPair() {
    const bakIp = this._signalInfo.bakRelayIps?.shift();
    if (bakIp) {
      bakIp.relayPort = bakIp.relayPort || this._signalInfo.relayPort;
    }
    return bakIp;
  }

  clearBakRelayIps() {
    this._signalInfo.bakRelayIps = [];
  }

  stopKeepAliveIn(delay = 3600) {
    if (this.keepAlive) {
      this._log.info(`stopKeepAlive in ${delay}s`);
      this.stopKeepAliveTimeout = setTimeout(() => {
        this.keepAlive = false;
        this._log.info(`close due to not used ${delay}s`);
        this.close();
        this.off(SignalEvent.JOIN_ROOM_RESULT, clearKeepAliveTimeout);
      }, delay * 1000);
      const clearKeepAliveTimeout = (event: any) => {
        if (event.data.code === 0) {
          this._log.info('stopKeepAlive clear timeout');
          clearTimeout(this.stopKeepAliveTimeout);
          this.off(SignalEvent.JOIN_ROOM_RESULT, clearKeepAliveTimeout);
        }
      };

      this.on(SignalEvent.JOIN_ROOM_RESULT, clearKeepAliveTimeout);
    }
  }

  stopPreconnectIn(delay = 300) {
    // 后台 keepAlive 优先级高于 preconnect
    if (this.keepAlive) return;
    this._log.info(`stopPreconnect in ${delay}s`);
    this.stopPreconnectTimeout = setTimeout(() => {
      this._log.info(`close preconnect due to not used in ${delay}s`);
      this.close();
      this.room.clearNetworkQuality();
      this.off(SignalEvent.JOIN_ROOM_RESULT, clearPreconnectTimeout);
    }, delay * 1000);
    const clearPreconnectTimeout = (event: any) => {
      if (event.data.code === 0) {
        this._log.info('stopPreconnect clear timeout');
        clearTimeout(this.stopPreconnectTimeout);
        this.off(SignalEvent.JOIN_ROOM_RESULT, clearPreconnectTimeout);
      }
    };

    this.on(SignalEvent.JOIN_ROOM_RESULT, clearPreconnectTimeout);
  }

  markPreconnectConnected(config: Omit<PreconnectConfig, 'linkedTime'>) {
    this._preconnectConfig = { ...config, linkedTime: Date.now() };
  }

  isPreconnectValid(sdkAppId: number, userId: string, userSig: string): boolean {
    if (!this.hasPreconnect || !this._preconnectConfig) return false;
    if ((sdkAppId !== this._preconnectConfig.sdkAppId) || (userId !== this._preconnectConfig.userId) || (userSig !== this._preconnectConfig.userSig)) {
      this._log.warn('preconnect params not match');
      return false;
    }
    if (!this.isConnected) {
      this._log.warn('preconnect is not connected');
      return false;
    }
    return true;
  }

  consumePreconnect() {
    this.hasPreconnect = false;
    this._preconnectConfig = undefined;
  }

  emitConnectionStateChanged(state: SignalConnectionState) {
    if (state === this._currentState) {
      return;
    }

    this._log.info(`${this._currentState} -> ${state}`);
    const event: {
      prevState: SignalConnectionState;
      state: SignalConnectionState;
      isReconnecting?: boolean;
    } = {
      prevState: this._currentState,
      state
    };
    if (state === SignalConnectionState.CONNECTING) {
      event.isReconnecting = this._isReconnecting;
    }
    this.emit(ChannelEvent.CONNECTION_STATE_CHANGED, event);

    this._currentState = state;
    if (state === SignalConnectionState.CONNECTED) {
      this.emit(ChannelEvent.CONNECTED);
    } else if (state === SignalConnectionState.DISCONNECTED) {
      this.emit(ChannelEvent.DISCONNECTED);
    }
  }
}
